/**
 * Social-privacy "levels" — web's mirror of the mobile app's Full / Limited /
 * Minimum visibility model.
 *
 * "Level" is purely client-derived: the backend stores only per-key booleans on
 * `SocialPreferencesVo` (POST `/users/{id}/social/sharing_preference`), with no
 * server notion of a level. Mobile's level system touches exactly NINE managed
 * keys and writes them as a locked group; the other six keys
 * (`top_holders`, `metric_roi`, `metric_dollars_traded`,
 * `metric_dollars_investment`, `metric_portfolio_value`,
 * `metric_signed_open_position`) are outside the managed level set and are
 * deliberately excluded from level derivation, matching mobile.
 *
 * `metric_dollars_traded` gates the profile-header "Volume" stat. Web writes it
 * alongside every level change, enabled at Full and Limited. Other orphan keys,
 * including `top_holders`, are neither derived nor written. This preserves old
 * settings and avoids sending options unsupported by the web API.
 */
import type { SocialPreferencesVo } from 'models';

/** A level the user can choose, and the only thing this app ever writes. */
export type SharingLevel = 'full' | 'limited' | 'minimum';

/**
 * What a *stored* preference map reads as — the three levels plus `custom`.
 *
 * `custom` means the stored keys match no level. Two ways to get there:
 *
 * - `useSetSharingLevel` POSTs the managed keys one at a time, so an interrupted write leaves a
 *   half-applied map;
 * - another client (mobile, an older build) wrote a combination web's three levels cannot express.
 *
 * It is derivation-only: never selectable, never written. Keeping it out of `SharingLevel` is what
 * makes that a compile-time guarantee — `getSharingLevelTargets` and `useSetSharingLevel` accept
 * only `SharingLevel`, so there is no way to ask for "write custom", and no runtime guard to
 * forget. Every consumer that renders a stored level takes `DerivedSharingLevel` instead.
 */
export type DerivedSharingLevel = SharingLevel | 'custom';

/** The nine keys mobile's level system manages. Orphan keys are excluded. */
export type ManagedSharingKey =
    | 'metric_num_markets_traded'
    | 'metric_volume'
    | 'metric_pnl'
    | 'metric_category_positive_pnl'
    | 'metric_category_pnl'
    | 'activity'
    | 'portfolio'
    | 'closed_portfolio'
    | 'metric_open_interest';

/**
 * Each level's enabled key set. Full and Limited mirror mobile. Web intentionally
 * makes Minimum an empty set so it is a real leaderboard opt-out; mobile's
 * current map leaves predictions enabled, which conflicts with this surface's
 * explicit Join/Leave contract.
 */
const LEVEL_ENABLED_KEYS: Record<SharingLevel, readonly ManagedSharingKey[]> = {
    full: [
        'metric_num_markets_traded',
        'metric_volume',
        'metric_pnl',
        'metric_category_positive_pnl',
        'metric_category_pnl',
        'activity',
        'portfolio',
        'closed_portfolio',
        'metric_open_interest',
    ],
    limited: [
        'metric_num_markets_traded',
        'metric_volume',
        'metric_pnl',
        'metric_category_positive_pnl',
        'metric_category_pnl',
    ],
    minimum: [],
};

/** Preferences that can place a user on a social statistics leaderboard. */
export const SOCIAL_LEADERBOARD_SHARING_KEYS: readonly ManagedSharingKey[] = [
    'metric_num_markets_traded',
    'metric_volume',
    'metric_pnl',
    'metric_category_positive_pnl',
    'metric_category_pnl',
];

/** Includes partial old-style configurations without migrating them. */
export function sharesSocialLeaderboardMetrics(prefs: SocialPreferencesVo): boolean {
    return SOCIAL_LEADERBOARD_SHARING_KEYS.some((key) => prefs[key]);
}

/** All nine managed keys (== the Full enabled set). */
export const MANAGED_SHARING_KEYS: readonly ManagedSharingKey[] = LEVEL_ENABLED_KEYS.full;

/**
 * Profile-header volume is shared at Full and Limited, and hidden at Minimum.
 * Keep this write-only key outside level derivation to preserve existing labels.
 */
const VOLUME_WRITE_KEY = 'metric_dollars_traded' satisfies keyof SocialPreferencesVo;

export type SharingLevelWriteKey = ManagedSharingKey | typeof VOLUME_WRITE_KEY;

/** Derivation checks levels highest-first and takes the first fully-satisfied set. */
const LEVELS_HIGH_TO_LOW: readonly SharingLevel[] = ['full', 'limited', 'minimum'];

/**
 * Full-overwrite target for a level: every managed key mapped to its boolean for
 * that level (enabled → `true`, otherwise `false`). Like mobile, web POSTs all
 * nine managed keys on every level change rather than sending a delta.
 *
 * Plus the write-only target `metric_dollars_traded`, enabled at Full and Limited.
 * Legacy orphan preferences such as `top_holders` are intentionally left untouched.
 */
export function getSharingLevelTargets(level: SharingLevel): Record<SharingLevelWriteKey, boolean> {
    const enabled = new Set<ManagedSharingKey>(LEVEL_ENABLED_KEYS[level]);
    const targets = MANAGED_SHARING_KEYS.reduce(
        (acc, key) => {
            acc[key] = enabled.has(key);
            return acc;
        },
        {} as Record<SharingLevelWriteKey, boolean>,
    );
    targets[VOLUME_WRITE_KEY] = level !== 'minimum';
    return targets;
}

/**
 * Derive what a stored preference map actually *is*: the level whose managed keys it matches
 * exactly, or `custom` when it matches none.
 *
 * Matching is exact over the nine `MANAGED_SHARING_KEYS` -- every key equal to that level's
 * target, the falses as well as the trues. That is a deliberate change from the previous "highest
 * level whose enabled keys are all true", which ignored extra keys being on and so mislabelled
 * every partial map:
 *
 * - all nine off except `activity` reported `minimum`, while the profile's activity feed was
 *   public. That is the reported bug, and it is exactly the shape an interrupted Full-to-Minimum
 *   write leaves, since `useSetSharingLevel` POSTs the keys one at a time.
 * - the five metric keys on plus `activity` reported `limited`, whose own copy promises "your
 *   trading activity stays hidden".
 *
 * `custom` is the only answer that is both honest and non-escalating. The tempting alternative --
 * falling back to `full` -- is unsafe: this value pre-selects the radio in
 * `SocialVisibilityLevelModal` and seeds `Profile`'s edit modal, and saving performs a FULL
 * OVERWRITE of every managed key, so one tap on Done would publish activity, portfolio and every
 * leaderboard metric the user never enabled.
 *
 * DIVERGES FROM MOBILE, on purpose. `social_profile.dart` lets extra enabled keys pass, so a map
 * web now calls `custom` may still show as Limited there. Mobile silently mislabels; naming the
 * level accurately is the whole job of this surface, so web prefers "we cannot name this" over
 * naming it wrongly.
 *
 * `metric_dollars_traded` stays outside derivation, as before -- a write-only key mobile dropped
 * from its level sets (see the module header). A profile at Full whose volume write never landed
 * still derives `full`, not `custom`: its volume stat is hidden, but all nine managed keys are
 * right, and pulling the orphan key in would relabel a large number of otherwise-consistent
 * profiles.
 *
 * NOTE: mobile defaults a profile with *no stored prefs* to `limited`. Web's default
 * `SocialPreferencesVo` is all-false (-> `minimum` here), so the "no prefs -> limited" default is a
 * load/migration concern for the caller, not this pure function.
 */
export function deriveSharingLevel(prefs: SocialPreferencesVo): DerivedSharingLevel {
    const matchesExactly = (level: SharingLevel): boolean => {
        const enabled = new Set<ManagedSharingKey>(LEVEL_ENABLED_KEYS[level]);
        return MANAGED_SHARING_KEYS.every((key) => !!prefs[key] === enabled.has(key));
    };
    return LEVELS_HIGH_TO_LOW.find(matchesExactly) ?? 'custom';
}

/**
 * Copy for the `custom` state. Not part of `SHARING_LEVEL_COPY`, which is keyed by the levels a
 * user can pick -- `custom` is a description of where they already are, never an option.
 */
export const CUSTOM_SHARING_LEVEL_COPY = {
    label: 'Custom',
    description: 'Your current settings do not match any option below. Choose one to replace them.',
};

/** Render order for the selector: most-shared first. */
export const SHARING_LEVEL_ORDER: readonly SharingLevel[] = ['full', 'limited', 'minimum'];

export interface SharingLevelCopy {
    label: string;
    description: string;
}

/** Base copy shared by profile visibility and leaderboard enrollment. */
export const SHARING_LEVEL_COPY: Record<SharingLevel, SharingLevelCopy> = {
    full: { label: 'Full', description: 'Your entire profile, including trading activity' },
    limited: {
        label: 'Limited',
        description: 'Your entire profile, but your trading activity stays hidden',
    },
    minimum: { label: 'Minimum', description: 'Posts and followers only' },
};

export function getSharingLevelDescription(level: SharingLevel, hasMarginAccount: boolean): string {
    const base = SHARING_LEVEL_COPY[level].description;

    if (level === 'full') {
        return hasMarginAccount
            ? `${base}. Eligible for all leaderboards.`
            : `${base}. Eligible for social leaderboards.`;
    }

    if (level === 'limited') {
        return hasMarginAccount
            ? `${base}. Eligible for social and Perps profit leaderboards.`
            : `${base}. Eligible for social leaderboards only.`;
    }

    return `${base}. Hidden from all leaderboards.`;
}
